import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dataService from "@/app/services/dataService";
import CommonHelper from "@/app/helpers/CommonHelper";
import { GenericDataDisplayTypeEnum } from "@/enums/genericDataDisplayType";
import GenericDataDisplay from "../shared/genericDataDisplay";
import { Column } from "@/app/interfaces/Column";
import SortConfig from "@/app/interfaces/SortConfig";
import { useCallback } from "react";
import { RewardsBalanceItem } from "@/app/interfaces/response/RewardsBalanceHistoryResponse";
import { SHORT_CASHING } from "@/app/constants/constants";

const BalanceAndUsage = () => {
  const blackBorder = "1px solid #000000";

  const { data: balanceData } = useQuery({
    queryKey: ["rewardsBalanceTotal"],
    queryFn: () => dataService.getRewardsBalanceTotal(),
    staleTime: SHORT_CASHING,
    gcTime: SHORT_CASHING,
  });

  const fetchRewardsBalanceHistory = useCallback(
    async (
      page: number,
      pageSize: number,
      externalFilters: any,
      sort?: SortConfig
    ): Promise<{ items: RewardsBalanceItem[]; total: number }> => {
      const res = await dataService.getRewardsBalanceHistory({
        page,
        pageSize,
        filter: {
          ...externalFilters,
        },
        sort: sort ? [sort] : [],
      });

      return {
        items: res.success ? res.data.data : [],
        total: res.success ? res.data.totalResults : 0,
      };
    },
    []
  );

  const columns: Column<RewardsBalanceItem>[] = [
    {
      id: "createdOnUtc",
      label: "Reward Earned Date",
      align: "left",
      sortable: true,
      render: (item: RewardsBalanceItem) => {
        if (!item.createdOnUtc) return "";
        const date = new Date(item.createdOnUtc);
        return new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(date);
      },
    },
    {
      id: "points",
      label: "Reward Amount",
      align: "left",
      sortable: true,
      render: (item: RewardsBalanceItem) =>
        CommonHelper.addDolarSign(Math.trunc(item.points), 0),
    },
    {
      id: "validPoints",
      label: "Remaining Balance",
      align: "left",
      sortable: true,
      render: (item: RewardsBalanceItem) =>
        CommonHelper.addDolarSign(Math.trunc(item.pointsBalance), 0),
    },
    {
      id: "endDateUtc",
      label: "Expiration Date",
      align: "left",
      sortable: true,
      render: (item: RewardsBalanceItem) => {
        if (!item.endDateUtc) return "";
        const date = new Date(item.endDateUtc);
        return new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(date);
      },
    },
    {
      id: "status",
      label: "Status",
      align: "left",
      sortable: true,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 0, md: 4 },
        margin: "0 auto",
        fontFamily: "sans-serif",
      }}
    >
      <Typography
        align="center"
        variant="body1"
        sx={{ fontWeight: "bold", mb: 3 }}
      >
        Total IMPACT Rewards balance:{" "}
        {CommonHelper.addDolarSign(balanceData?.data ?? 0, 0)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontStyle: "italic", color: "#555", mb: 2, textAlign: "center" }}
      >
        NOTE: The IMPACT Rewards information below is updated once daily at
        approx. 6:00 am EST. Orders affecting IMPACT Rewards after 6:00 am EST
        may not be displayed in real time.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <GenericDataDisplay<RewardsBalanceItem>
          queryKeyBase={["impactRewards"]}
          fetchPage={fetchRewardsBalanceHistory}
          columns={columns}
          mode={GenericDataDisplayTypeEnum.TABLE}
          noDataMessage="No items to display"
          defaultPageSize={100}
          wrapperStyleProps={{ sx: { width: "100%" } }}
          externalFilters={{ year: 0 }}
          defaultSort={{
            field: "createdOnUtc",
            direction: "desc",
          }}
          isFirstColumnSticky={true}
        />
      </Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ maxWidth: 600, mx: "auto", border: blackBorder, borderRadius: 0 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: "#0057b8",
                  color: "white",
                  fontWeight: "bold",
                  width: "30%",
                  borderRight: blackBorder,
                  borderBottom: blackBorder,
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#0057b8",
                  color: "white",
                  fontWeight: "bold",
                  borderBottom: blackBorder,
                }}
              >
                Meaning
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { s: "Active", m: "IMPACT Rewards that can be used." },
              {
                s: "Redeemed",
                m: "IMPACT Rewards that have been used on an order.",
              },
              {
                s: "Partially Redeemed",
                m: "Check the Remaining Balance column to view how much of your reward is still available.",
              },
              {
                s: "Reserved",
                m: "IMPACT Rewards that have been used for an order that is not yet invoiced or paid.",
              },
              {
                s: "SO",
                m: "Stock Order the IMPACT Rewards reward has been used on.",
              },
            ].map((row, index, array) => (
              <TableRow key={row.s}>
                <TableCell
                  sx={{
                    borderRight: blackBorder,
                    borderBottom:
                      index !== array.length - 1 ? blackBorder : "none",
                  }}
                >
                  {row.s}
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom:
                      index !== array.length - 1 ? blackBorder : "none",
                  }}
                >
                  {row.m}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BalanceAndUsage;
